export type LocationLike = Pick<
    Location,
    'origin' | 'pathname' | 'search' | 'hash'
>;

// Adyen appends `redirectResult` to the returnUrl on redirect-back.
// `paymentToken` is set by us in buildReturnUrl(). `webPageToken` is a
// legacy param that only marks a redirect return for cleanup/detection.
const REDIRECT_RESULT_PARAM = 'redirectResult';
const PAYMENT_TOKEN_PARAM = 'paymentToken';
const REDIRECT_PARAM_NAMES = [
    REDIRECT_RESULT_PARAM,
    PAYMENT_TOKEN_PARAM,
    'webPageToken'
] as const;

interface UrlParts {
    path: string;
    delimiter: '' | '?' | '&';
    rawParams: string;
    params: URLSearchParams;
}

export interface RedirectParams {
    redirectResult: string | null;
    paymentToken: string | null;
    hasRedirectParameters: boolean;
    redirectResultInQuery: boolean;
    paymentTokenInQuery: boolean;
    hasHashRoute: boolean;
}

function splitHash(hash: string): UrlParts | null {
    if (!hash) {
        return null;
    }

    const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
    const directParams = normalizedHash.startsWith('?')
        ? normalizedHash.slice(1)
        : normalizedHash;
    const startsWithRedirectParam = REDIRECT_PARAM_NAMES.some((name) =>
        directParams.startsWith(`${name}=`)
    );

    if (startsWithRedirectParam) {
        return {
            path: '',
            delimiter: normalizedHash.startsWith('?') ? '?' : '',
            rawParams: directParams,
            params: new URLSearchParams(directParams)
        };
    }

    const queryStart = normalizedHash.search(/[?&]/);
    if (queryStart === -1) {
        return {
            path: normalizedHash,
            delimiter: '',
            rawParams: '',
            params: new URLSearchParams()
        };
    }

    const rawParams = normalizedHash.substring(queryStart + 1);
    return {
        path: normalizedHash.substring(0, queryStart),
        delimiter: normalizedHash[queryStart] as '?' | '&',
        rawParams,
        params: new URLSearchParams(rawParams)
    };
}

const hasAnyRedirectParam = (params: URLSearchParams) =>
    REDIRECT_PARAM_NAMES.some((name) => params.has(name));

export function getRedirectParamsFromLocation(
    search: string,
    hash: string
): RedirectParams {
    const queryParams = new URLSearchParams(search);
    const hashParams = splitHash(hash)?.params;
    const getParam = (name: 'redirectResult' | 'paymentToken') =>
        queryParams.get(name) || hashParams?.get(name) || null;

    return {
        redirectResult: getParam(REDIRECT_RESULT_PARAM),
        paymentToken: getParam(PAYMENT_TOKEN_PARAM),
        hasRedirectParameters:
            hasAnyRedirectParam(queryParams) ||
            Boolean(hashParams && hasAnyRedirectParam(hashParams)),
        redirectResultInQuery: queryParams.has(REDIRECT_RESULT_PARAM),
        paymentTokenInQuery: queryParams.has(PAYMENT_TOKEN_PARAM),
        hasHashRoute: Boolean(hash)
    };
}

export function getRedirectResultFromLocation(
    search: string,
    hash: string
): string | null {
    return getRedirectParamsFromLocation(search, hash).redirectResult;
}

const withoutRedirectParams = (params: URLSearchParams): URLSearchParams => {
    REDIRECT_PARAM_NAMES.forEach((name) => params.delete(name));
    return params;
};

// Names are compared after decoding so that removal matches the detection
// done by URLSearchParams, while the untouched value bytes are preserved.
const decodeParamName = (name: string): string => {
    try {
        return decodeURIComponent(name.replace(/\+/g, ' '));
    } catch {
        return name;
    }
};

const withoutRawRedirectParams = (rawParams: string): string =>
    rawParams
        .split('&')
        .filter((param) => {
            const separatorIndex = param.indexOf('=');
            const name = decodeParamName(
                separatorIndex === -1
                    ? param
                    : param.substring(0, separatorIndex)
            );
            return !REDIRECT_PARAM_NAMES.some(
                (redirectParamName) => redirectParamName === name
            );
        })
        .join('&');

export function cleanHashParams(hash: string): string {
    const hashParts = splitHash(hash);
    if (!hashParts) {
        return '';
    }
    if (!hasAnyRedirectParam(hashParts.params)) {
        return hash;
    }

    const { path, delimiter, rawParams } = hashParts;
    const remaining = withoutRawRedirectParams(rawParams);
    const cleaned = remaining ? `${path}${delimiter}${remaining}` : path;

    return cleaned ? `#${cleaned}` : '';
}

export function cleanRedirectParamsFromLocation(
    locationLike: LocationLike
): string {
    const { origin, pathname, search, hash } = locationLike;

    const cleanQuery = withoutRedirectParams(
        new URLSearchParams(search)
    ).toString();
    const cleanHash = cleanHashParams(hash);

    return `${origin}${pathname}${cleanQuery ? `?${cleanQuery}` : ''}${cleanHash}`;
}

export function appendPaymentToken(
    baseUrl: string,
    paymentToken?: string | null
): string {
    const url = new URL(baseUrl);

    if (paymentToken) {
        url.searchParams.set(PAYMENT_TOKEN_PARAM, paymentToken);
    }

    return url.toString();
}
